import dayjs, { Dayjs } from 'dayjs';

/**
 * Converte código de idioma do sistema ('pt-br', 'es-ar') para locale do Intl ('pt-BR', 'en-US')
 * @note 'es-ar' retorna 'en-US' pois a Argentina usa formato de data igual ao inglês (MM/DD/YYYY)
 */
export const obterLocaleDoIdioma = (languageCode: string): string => {
  const languageMap: { [key: string]: string } = {
    'pt-br': 'pt-BR',
    'es-ar': 'en-US',  // Argentina usa formato inglês
    'en-us': 'en-US'
  };
  
  return languageMap[languageCode.toLowerCase()] || 'pt-BR';
};

/**
 * Obtém o formato de data do dayjs baseado no idioma
 * @note 'es-ar' usa 'MM/DD/YYYY' (formato inglês) conforme solicitado pela área de produtos
 */
export const obterFormatoDataDayjs = (languageCode: string): string => {
  const formatMap: { [key: string]: string } = {
    'pt-br': 'DD/MM/YYYY',
    'es-ar': 'MM/DD/YYYY',
    'en-us': 'MM/DD/YYYY'
  };
  
  return formatMap[languageCode.toLowerCase()] || 'DD/MM/YYYY';
};

/**
 * Obtém o locale do dayjs baseado no idioma
 * @note 'es-ar' retorna 'en' pois a Argentina usa formato de data igual ao inglês
 */
export const obterLocaleDayjs = (languageCode: string): string => {
  const localeMap: { [key: string]: string } = {
    'pt-br': 'pt-br',
    'es-ar': 'en',  // Argentina usa locale inglês
    'en-us': 'en'
  };
  
  return localeMap[languageCode.toLowerCase()] || 'pt-br';
};

/**
 * Formata uma data de acordo com o locale configurado
 * Aceita código de idioma ('pt-br', 'es-ar') ou locale ('pt-BR', 'en-US')
 * Aceita também objetos Dayjs
 */
export const formatarData = (
  data: Date | string | number | Dayjs | null | undefined,
  languageCodeOrLocale: string = 'pt-BR',
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!data) return '';
  
  try {
    // Se for Dayjs, converte para Date
    let dataObj: Date;
    if (dayjs.isDayjs(data)) {
      dataObj = data.toDate();
    } else if (typeof data === 'string' || typeof data === 'number') {
      dataObj = new Date(data);
    } else {
      dataObj = data;
    }
    
    if (isNaN(dataObj.getTime())) return '';
    
    // Se for código de idioma (contém hífen minúsculo), converte para locale
    const locale = languageCodeOrLocale.includes('-') && 
                   languageCodeOrLocale === languageCodeOrLocale.toLowerCase()
      ? obterLocaleDoIdioma(languageCodeOrLocale)
      : languageCodeOrLocale;
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    };
    
    return dataObj.toLocaleDateString(locale, options || defaultOptions);
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '';
  }
};

/**
 * Formata uma data com hora de acordo com o locale configurado
 * Aceita também objetos Dayjs
 */
export const formatarDataHora = (
  data: Date | string | number | Dayjs | null | undefined,
  languageCodeOrLocale: string = 'pt-BR'
): string => {
  return formatarData(data, languageCodeOrLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formata uma data com hora e segundos de acordo com o locale configurado
 * Aceita também objetos Dayjs
 */
export const formatarDataHoraSegundos = (
  data: Date | string | number | Dayjs | null | undefined,
  languageCodeOrLocale: string = 'pt-BR'
): string => {
  return formatarData(data, languageCodeOrLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

/**
 * Formata apenas dia e mês de acordo com o locale configurado
 * Aceita também objetos Dayjs
 */
export const formatarDiaMes = (
  data: Date | string | number | Dayjs | null | undefined,
  languageCodeOrLocale: string = 'pt-BR'
): string => {
  if (!data) return '';
  
  try {
    // Se for Dayjs, converte para Date
    let dataObj: Date;
    if (dayjs.isDayjs(data)) {
      dataObj = data.toDate();
    } else if (typeof data === 'string' || typeof data === 'number') {
      dataObj = new Date(data);
    } else {
      dataObj = data;
    }
    
    if (isNaN(dataObj.getTime())) return '';
    
    const locale = languageCodeOrLocale.includes('-') && 
                   languageCodeOrLocale === languageCodeOrLocale.toLowerCase()
      ? obterLocaleDoIdioma(languageCodeOrLocale)
      : languageCodeOrLocale;
    
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit'
    };
    
    return dataObj.toLocaleDateString(locale, options);
  } catch (error) {
    console.error('Erro ao formatar dia/mês:', error);
    return '';
  }
};

/**
 * Formata apenas a hora de acordo com o locale configurado
 * Aceita também objetos Dayjs
 */
export const formatarHora = (
  data: Date | string | number | Dayjs | null | undefined,
  languageCodeOrLocale: string = 'pt-BR',
  incluirSegundos: boolean = false
): string => {
  if (!data) return '';
  
  try {
    // Se for Dayjs, converte para Date
    let dataObj: Date;
    if (dayjs.isDayjs(data)) {
      dataObj = data.toDate();
    } else if (typeof data === 'string' || typeof data === 'number') {
      dataObj = new Date(data);
    } else {
      dataObj = data;
    }
    
    if (isNaN(dataObj.getTime())) return '';
    
    const locale = languageCodeOrLocale.includes('-') && 
                   languageCodeOrLocale === languageCodeOrLocale.toLowerCase()
      ? obterLocaleDoIdioma(languageCodeOrLocale)
      : languageCodeOrLocale;
    
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      ...(incluirSegundos && { second: '2-digit' })
    };
    
    return dataObj.toLocaleTimeString(locale, options);
  } catch (error) {
    console.error('Erro ao formatar hora:', error);
    return '';
  }
};

